import { api } from "../../lib";
import {
  requireDateOnlyString,
  type DateOnlyString,
} from "../../utils/dateOnly";

export interface AuditLogUser {
  id: string;
  username: string;
  fullName: string;
}

export interface AuditLogEntry {
  id: string;
  userId: string;
  user: AuditLogUser;
  action: string;
  resourceType: string;
  resourceId: string | null;
  details: Record<string, any> | null;
  ipAddress: string;
  userAgent: string;
  url?: string | null;
  createdAt: string;
}

export interface AuditLogFilters {
  userId?: string;
  action?: string;
  resourceType?: string;
  resourceId?: string;
  startDate?: DateOnlyString;
  endDate?: DateOnlyString;
  page?: number;
  limit?: number;
}

export interface PaginatedAuditLogs {
  data: AuditLogEntry[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const auditLogService = {
  getAll: async (filters: AuditLogFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.userId) params.set("userId", filters.userId);
    if (filters.action) params.set("action", filters.action);
    if (filters.resourceType) params.set("resourceType", filters.resourceType);
    if (filters.resourceId) params.set("resourceId", filters.resourceId);
    if (filters.startDate)
      params.set(
        "startDate",
        requireDateOnlyString(filters.startDate, "Audit start date")
      );
    if (filters.endDate)
      params.set(
        "endDate",
        requireDateOnlyString(filters.endDate, "Audit end date")
      );
    if (filters.page) params.set("page", String(filters.page));
    if (filters.limit) params.set("limit", String(filters.limit));

    const query = params.toString();
    return api.get<PaginatedAuditLogs>(
      `/admin/audit-logs${query ? `?${query}` : ""}`
    );
  },

  exportUrl: (filters: AuditLogFilters = {}): string => {
    const params = new URLSearchParams();
    if (filters.userId) params.set("userId", filters.userId);
    if (filters.action) params.set("action", filters.action);
    if (filters.resourceType) params.set("resourceType", filters.resourceType);
    if (filters.resourceId) params.set("resourceId", filters.resourceId);
    if (filters.startDate)
      params.set(
        "startDate",
        requireDateOnlyString(filters.startDate, "Audit start date")
      );
    if (filters.endDate)
      params.set(
        "endDate",
        requireDateOnlyString(filters.endDate, "Audit end date")
      );
    const base = process.env.REACT_APP_API_BASE_URL || "";
    const query = params.toString();
    return `${base}/lis/admin/audit-logs/export${query ? `?${query}` : ""}`;
  },

  getForResource: async (type: string, id: string) => {
    return api.get<AuditLogEntry[]>(`/admin/audit-logs/resource/${type}/${id}`);
  },

  getForUser: async (userId: string) => {
    return api.get<AuditLogEntry[]>(`/admin/audit-logs/user/${userId}`);
  },

  getMyActivity: async (filters: Omit<AuditLogFilters, "userId"> = {}) => {
    const params = new URLSearchParams();
    if (filters.page) params.set("page", String(filters.page));
    if (filters.limit) params.set("limit", String(filters.limit));
    if (filters.startDate)
      params.set(
        "startDate",
        requireDateOnlyString(filters.startDate, "Activity start date")
      );
    if (filters.endDate)
      params.set(
        "endDate",
        requireDateOnlyString(filters.endDate, "Activity end date")
      );
    const query = params.toString();
    return api.get<PaginatedAuditLogs>(`/audit/me${query ? `?${query}` : ""}`);
  },
};
